import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Divider,
  Field,
  Input,
  Link,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import { Shell, BackFooter } from '../components/Shell';
import { Steps, Title, Muted } from '../components/ui';
import { BandPicker } from '../components/BandPicker';
import { checkWorkEmail } from '../lib/emailRules';
import { postJson, ApiError } from '../lib/api';
import { getMsProfile, signIn, signinClientId, signOut, type MsProfile } from '../lib/msAuth';
import { HEARD_FROM, useSignup, type SignupDetails, type TenantBand } from '../lib/signupState';

const useStyles = makeStyles({
  form: { display: 'grid', gap: '16px' },
  ms: { display: 'grid', gap: '8px' },
  msButton: { minHeight: '48px', fontSize: tokens.fontSizeBase400, columnGap: '10px' },
  submit: { minHeight: '48px', fontSize: tokens.fontSizeBase400 },
  check: { alignItems: 'flex-start' },
});

type Errors = Partial<Record<'name' | 'email' | 'mobile' | 'mspName' | 'tenantBand', string>>;

const empty: SignupDetails = {
  name: '',
  email: '',
  mobile: '',
  mspName: '',
  jobTitle: '',
  tenantBand: '',
  heardFrom: 'Kaseya Connect',
  marketingOptIn: false,
};

function mobileError(value: string): string {
  const v = value.trim();
  if (!v) return 'Enter your mobile number.';
  return /^\+?\d{8,15}$/.test(v.replace(/[\s()-]/g, '')) ? '' : 'Enter a valid mobile number, e.g. 0412 345 678.';
}

async function validateField(name: keyof Errors, d: SignupDetails): Promise<string> {
  switch (name) {
    case 'name':
      return d.name.trim().length >= 2 ? '' : 'Enter your name.';
    case 'email':
      return checkWorkEmail(d.email);
    case 'mobile':
      return mobileError(d.mobile);
    case 'mspName':
      return d.mspName.trim() ? '' : "Enter your MSP's name.";
    case 'tenantBand':
      return d.tenantBand ? '' : 'Pick how many client tenants you manage.';
  }
}

const FIELDS: (keyof Errors)[] = ['name', 'email', 'mobile', 'mspName', 'tenantBand'];

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

export function Details() {
  const s = useStyles();
  const navigate = useNavigate();
  const { state, update } = useSignup();
  const [d, setD] = useState<SignupDetails>(() => ({ ...empty, ...state.details }));
  const [errors, setErrors] = useState<Errors>(() =>
    state.serverError ? { [state.serverError.field]: state.serverError.message } : {},
  );
  const [ms, setMs] = useState<MsProfile | null>(null);
  const [msAvailable, setMsAvailable] = useState(false);
  const [msBusy, setMsBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  // Arrived by personalised link: details come from Pipedrive, no Microsoft sign-in needed.
  const linked = Boolean(state.token);
  const refs = useRef<Partial<Record<keyof Errors, HTMLElement | null>>>({});

  // Clear a server-side error once it has been shown.
  useEffect(() => {
    if (state.serverError) update({ serverError: undefined });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefill from the Entra profile after "Continue with Microsoft". Name and
  // email come from Microsoft; the rest only fills what's still empty.
  useEffect(() => {
    if (linked) return;
    signinClientId().then((id) => setMsAvailable(Boolean(id)));
    getMsProfile(Boolean(state.msIdToken)).then((p) => {
      if (!p) return;
      setMs(p);
      update({ msIdToken: p.idToken });
      setD((cur) => ({
        ...cur,
        name: p.name || cur.name,
        email: p.email || cur.email,
        mobile: cur.mobile || p.mobile,
        mspName: cur.mspName || p.company,
        jobTitle: cur.jobTitle || p.jobTitle,
      }));
      // Usually all that's left is the number of client tenants.
      setTimeout(() => refs.current.tenantBand?.closest('fieldset, div')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const msVerified = Boolean(ms && ms.email.toLowerCase() === d.email.trim().toLowerCase());

  async function continueWithMicrosoft() {
    setMsBusy(true);
    update({ details: d }); // keep anything already typed across the redirect
    try {
      await signIn();
    } catch {
      setMsBusy(false);
      setFormError("Microsoft sign-in didn't start. Fill in the form instead.");
    }
  }

  async function notYou() {
    update({ msIdToken: undefined, details: { ...empty } });
    await signOut('/details');
  }

  function set<K extends keyof SignupDetails>(key: K, value: SignupDetails[K]) {
    const next = { ...d, [key]: value };
    setD(next);
    // Clear an error as soon as it's fixed, so the layout doesn't jump on blur.
    if (key in errors && errors[key as keyof Errors]) {
      validateField(key as keyof Errors, next).then((msg) => setErrors((e) => ({ ...e, [key]: msg })));
    }
  }

  async function onBlur(name: keyof Errors) {
    if (!d[name]) return;
    const msg = await validateField(name, d);
    setErrors((e) => ({ ...e, [name]: msg }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const results = await Promise.all(FIELDS.map((f) => validateField(f, d)));
    const next = Object.fromEntries(FIELDS.map((f, i) => [f, results[i]])) as Errors;
    setErrors(next);
    const firstBad = FIELDS.find((f) => next[f]);
    if (firstBad) {
      refs.current[firstBad]?.focus();
      return;
    }
    const details = {
      ...d,
      name: d.name.trim(),
      email: d.email.trim(),
      mobile: d.mobile.trim(),
      mspName: d.mspName.trim(),
      jobTitle: d.jobTitle.trim(),
    };
    if (linked) {
      update({ details });
      navigate('/terms');
      return;
    }
    // Save to Pipedrive straight away, so a lead captured at the table isn't
    // lost if they stop here. The next screen offers to finish now or later.
    setFormError('');
    setBusy(true);
    try {
      const { token } = await postJson<{ token: string | null }>('/api/capture', {
        ...details,
        msIdToken: msVerified ? ms!.idToken : undefined,
      });
      update({ details, token: token ?? undefined, captured: true });
      navigate('/saved');
    } catch (err) {
      setBusy(false);
      const e = err as ApiError;
      if (e.field && FIELDS.includes(e.field as keyof Errors)) {
        setErrors((cur) => ({ ...cur, [e.field!]: e.message }));
        refs.current[e.field as keyof Errors]?.focus();
      } else {
        setFormError(e.message);
      }
    }
  }

  const err = (f: keyof Errors) => (errors[f] ? { validationState: 'error' as const, validationMessage: errors[f] } : {});

  return (
    <Shell
      labelledBy="title"
      brandTitle="Tell us who you are"
      brandTagline="We match your work email to your Microsoft 365 tenant and send your findings there."
      footerLeft={<BackFooter to="/" />}
    >
      <Steps step={1} of={3} />
      <Title id="title">{linked && !state.captured ? 'Check your details' : 'Your details'}</Title>
      {linked && !state.captured && <Muted>This is what you gave us at Kaseya Connect. Fix anything that's wrong.</Muted>}

      {!linked && !ms && msAvailable && (
        <div className={s.ms}>
          <Button
            className={s.msButton}
            size="large"
            icon={msBusy ? <Spinner size="tiny" /> : <MicrosoftLogo />}
            disabled={msBusy}
            onClick={continueWithMicrosoft}
          >
            {msBusy ? 'Opening Microsoft…' : 'Continue with Microsoft'}
          </Button>
          <Muted>Fills in your details from your Microsoft 365 profile, so you only pick how many client tenants you manage.</Muted>
          <Divider>or fill in the form</Divider>
        </div>
      )}
      {ms && (
        <MessageBar intent="success">
          <MessageBarBody>
            Signed in as {ms.email}. We've filled in what your Microsoft 365 profile has; check it and pick how many client
            tenants you manage. <Link onClick={notYou}>Not you?</Link>
          </MessageBarBody>
        </MessageBar>
      )}

      <form className={s.form} noValidate onSubmit={onSubmit}>
        <Field label="Your name" required {...err('name')}>
          <Input
            ref={(el) => {
              refs.current.name = el;
            }}
            size="large"
            autoComplete="name"
            maxLength={100}
            value={d.name}
            onChange={(_, v) => set('name', v.value)}
            onBlur={() => onBlur('name')}
          />
        </Field>

        <Field
          label="Work email"
          required
          hint={
            msVerified
              ? 'Verified with Microsoft.'
              : linked
                ? 'Finishing this for a colleague? Change it to your own work email.'
                : "Use the address on your MSP's own Microsoft 365 tenant."
          }
          {...err('email')}
        >
          <Input
            ref={(el) => {
              refs.current.email = el;
            }}
            size="large"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={254}
            readOnly={msVerified}
            value={d.email}
            onChange={(_, v) => set('email', v.value)}
            onBlur={() => onBlur('email')}
          />
        </Field>

        <Field label="Mobile" required {...err('mobile')}>
          <Input
            ref={(el) => {
              refs.current.mobile = el;
            }}
            size="large"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            maxLength={20}
            placeholder="04xx xxx xxx"
            value={d.mobile}
            onChange={(_, v) => set('mobile', v.value)}
            onBlur={() => onBlur('mobile')}
          />
        </Field>

        <Field label="MSP name" required {...err('mspName')}>
          <Input
            ref={(el) => {
              refs.current.mspName = el;
            }}
            size="large"
            autoComplete="organization"
            maxLength={120}
            value={d.mspName}
            onChange={(_, v) => set('mspName', v.value)}
            onBlur={() => onBlur('mspName')}
          />
        </Field>

        <Field label="Job title" hint="Optional.">
          <Input
            size="large"
            autoComplete="organization-title"
            maxLength={100}
            value={d.jobTitle}
            onChange={(_, v) => set('jobTitle', v.value)}
          />
        </Field>

        <div
          ref={(el) => {
            refs.current.tenantBand = el?.querySelector('input') ?? null;
          }}
        >
          <BandPicker value={d.tenantBand} onChange={(v: TenantBand) => set('tenantBand', v)} error={errors.tenantBand} />
        </div>

        <Field label="How did you hear about us?">
          <Select size="large" value={d.heardFrom} onChange={(_, v) => set('heardFrom', v.value)}>
            {HEARD_FROM.map((h) => (
              <option key={h.value} value={h.value}>
                {h.label}
              </option>
            ))}
          </Select>
        </Field>

        <Checkbox
          className={s.check}
          checked={d.marketingOptIn}
          onChange={(_, v) => set('marketingOptIn', Boolean(v.checked))}
          label="Send me governance tips and follow-ups on my findings. Unsubscribe any time."
        />

        {formError && (
          <MessageBar intent="error">
            <MessageBarBody>{formError}</MessageBarBody>
          </MessageBar>
        )}

        <Button
          appearance="primary"
          size="large"
          type="submit"
          className={s.submit}
          disabled={busy}
          icon={busy ? <Spinner size="tiny" appearance="inverted" /> : undefined}
        >
          {busy ? 'Saving…' : 'Continue'}
        </Button>
      </form>
    </Shell>
  );
}
